import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import ItemList from '../../components/ItemList/ItemList';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

// Estilos
import styles from './participantedatapage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useParticipanteService } from '../../services/participanteServices';
import { useResultadoTesteService } from '../../services/resultadoTesteServices';

function ParticipanteDataPage() {
    const { idEmpresa, idTurma, idParticipante } = useParams();
    const navigate = useNavigate();

    const { logout } = useAuth();
    const { showToast } = useToast();
    const { showModal } = useModal();
    const { showLoader, hideLoader } = useLoader();

    const { obterEmpresaPorId } = useEmpresaService();
    const { obterTurmaPorId } = useTurmaService();
    const { obterParticipantePorId } = useParticipanteService();
    const { listarResultadosPorParticipante, deletarResultado, obterResultadoPorId } = useResultadoTesteService();

    const [empresa, setEmpresa] = useState(null);
    const [turma, setTurma] = useState(null);
    const [participante, setParticipante] = useState(null);
    const [resultados, setResultados] = useState([]);

    const [modalAberto, setModalAberto] = useState(false);
    const [dadosResultado, setDadosResultado] = useState(null);

    useEffect(() => {
        const carregarDados = async () => {
            showLoader("Carregando dados");

            try {
                const empresaResponse = await obterEmpresaPorId(idEmpresa);
                if (!empresaResponse.success) throw new Error('Erro ao carregar empresa');
                setEmpresa(empresaResponse.data);

                const turmaResponse = await obterTurmaPorId(idTurma);
                if (!turmaResponse.success) throw new Error('Erro ao carregar turma');
                setTurma(turmaResponse.data);

                const participanteResponse = await obterParticipantePorId(idParticipante);
                if (!participanteResponse.success) throw new Error('Erro ao carregar participante');
                setParticipante(participanteResponse.data);

                const resultadosResponse = await listarResultadosPorParticipante(idParticipante);
                if (resultadosResponse.success && resultadosResponse.resultados) {
                    const filtrados = resultadosResponse.resultados.filter(
                        (r) => String(r.idTurma) === String(idTurma)
                    );

                    const dadosFormatados = filtrados.map((r) => ({
                        id: r.id,
                        name: `${r.nomeTeste} (${new Date(r.dataEnvio).toLocaleDateString()})`,
                    }));

                    setResultados(dadosFormatados);
                } else {
                    showToast('Erro ao carregar resultados.', 3000, 'error');
                }
            } catch (error) {
                showToast(error.message, 3000, 'error');
                logout();
            }

            hideLoader();
        };

        if (idEmpresa && idTurma && idParticipante) carregarDados();
    }, [idEmpresa, idTurma, idParticipante]);

    const handleSelecionar = async (item) => {
        showLoader("Carregando resposta");
        try {
            const res = await obterResultadoPorId(item.id);
            if (res.success && res.data) {
                setDadosResultado(res.data);
                setModalAberto(true);
            } else {
                showToast("Erro ao carregar o resultado.", 3000, 'error');
            }
        } catch (error) {
            console.error("Erro ao obter resultado:", error);
            showToast("Erro ao carregar o resultado.", 3000, 'error');
        }
        hideLoader();
    };

    const handleEditar = (item) => {
        showToast('Impossível editar. Exclua a resposta para refazer o teste.', 3000, 'info');
    };

    const handleExcluir = (item) => {
        showModal(
            `Excluir ${item.name}?`,
            'Essa ação não poderá ser desfeita.',
            async () => {
                showLoader("Excluindo resultado");

                try {
                    const res = await deletarResultado(item.id);
                    if (res.success) {
                        setResultados((prev) => prev.filter((e) => e.id !== item.id));
                        showToast('Resultado excluído com sucesso!', 3000, 'success');
                    } else {
                        showToast('Erro ao excluir resultado.', 3000, 'error');
                        if (res.message?.toLowerCase().includes('token')) logout();
                    }
                } catch (error) {
                    showToast('Erro ao excluir resultado.', 3000, 'error');
                    console.error(error);
                }

                hideLoader();
            },
            () => {
                showToast('Exclusão cancelada.', 2000);
            }
        );
    };

    const handleAdd = () => {
        showToast('Calma! Quem responde os testes são os participantes.', 3000);
    };

    const breadcrumbIds = (empresa && turma && participante)
        ? [
            { id: idEmpresa, label: empresa.nome },
            { id: idTurma, label: turma.nome },
            { id: idParticipante, label: participante.nome }
        ]
        : [];

    return (
        <div className={styles.ParticipanteDataPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbIds} />

                <h1 className={styles.title}>Resultados de {participante?.nome || ''}</h1>
                <FriendlyPhrase page={"participanteRespostas"} />
            </div>

            <div className={styles.containerList}>
                <ItemList
                    items={resultados}
                    onAdd={handleAdd}
                    onEdit={handleEditar}
                    onDelete={handleExcluir}
                    onClickItem={handleSelecionar}
                />
            </div>

            {modalAberto && dadosResultado && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <h2 className={styles.modalTitle}>Respostas do Teste</h2>

                        <div className={styles.modalInfo}>
                            <p><strong>Data de Envio:</strong> {new Date(dadosResultado.data_envio).toLocaleString()}</p>
                        </div>

                        <div className={styles.modalRespostas}>
                            {JSON.parse(dadosResultado.resposta).map((item, index) => (
                                <div key={index} className={styles.respostaItem}>
                                    <p><strong>Pergunta:</strong> {item.pergunta}</p>
                                    <p><strong>Resposta:</strong> {item.resposta}</p>
                                    <p><strong>Descrição:</strong> {item.textoResposta}</p>
                                    <hr />
                                </div>
                            ))}
                        </div>

                        <button
                            className={styles.fecharBotao}
                            onClick={() => setModalAberto(false)}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
}

export default ParticipanteDataPage;